/**
 * The error envelope every ThuisCoach endpoint returns.
 *
 * Mirrors App\Support\Http\ApiErrorCode on the backend. Clients branch on
 * `code`; `message` is Dutch prose meant for a human and may be reworded freely.
 */
export type ApiErrorCode =
    | 'validation_failed'
    | 'unauthenticated'
    | 'forbidden'
    | 'not_found'
    | 'method_not_allowed'
    | 'conflict'
    | 'too_many_requests'
    | 'payload_too_large'
    | 'integration_unavailable'
    | 'server_error'
    | 'network_error';

export interface ApiErrorBody {
    code: ApiErrorCode;
    message: string;
    /** Present only for `validation_failed`: field name → Dutch messages. */
    errors?: Record<string, string[]>;
    /** Present on 429. Seconds until the next attempt is allowed. */
    retry_after?: number;
}

/**
 * Every failure the API layer surfaces, including transport failures, so a
 * caller only ever has to handle one error type.
 */
export class ApiError extends Error {
    readonly status: number;
    readonly code: ApiErrorCode;
    readonly fieldErrors: Record<string, string[]>;
    readonly retryAfter: number | undefined;

    constructor(status: number, body: ApiErrorBody) {
        super(body.message);
        this.name = 'ApiError';
        this.status = status;
        this.code = body.code;
        this.fieldErrors = body.errors ?? {};
        this.retryAfter = body.retry_after;
    }

    get isValidation(): boolean {
        return this.code === 'validation_failed';
    }

    get isUnauthenticated(): boolean {
        return this.code === 'unauthenticated';
    }

    /**
     * Whether retrying the exact same request could plausibly succeed. Used by
     * the query client to decide what is worth retrying.
     */
    get isRetryable(): boolean {
        return this.code === 'network_error' || this.status >= 500;
    }

    /** The first message for a field, for inline form errors. */
    fieldError(field: string): string | undefined {
        return this.fieldErrors[field]?.[0];
    }
}

/**
 * Narrows an unknown response body to the error envelope.
 *
 * A proxy, a crashed container or a misconfigured gateway can all produce a
 * non-JSON body, so this never assumes the shape it hopes for.
 */
/**
 * Whether a thrown value is the API saying "this does not exist".
 *
 * Worth a named helper because the alternative is what the exercise handbook
 * shipped: `query.isError` rendering "Deze oefening bestaat niet", so a dropped
 * connection or a 500 told the reader their exercise had been deleted and sent
 * them back to the index. A wrong explanation is worse than a vague one — it
 * makes the user act on it.
 */
export function isNotFound(error: unknown): boolean {
    return error instanceof ApiError && error.code === 'not_found';
}

export function toApiError(status: number, body: unknown): ApiError {
    if (typeof body === 'object' && body !== null && 'code' in body && 'message' in body) {
        const candidate = body as Partial<ApiErrorBody>;

        if (typeof candidate.code === 'string' && typeof candidate.message === 'string') {
            return new ApiError(status, candidate as ApiErrorBody);
        }
    }

    return new ApiError(status, {
        code: codeForStatus(status),
        message: 'Er ging iets mis bij het verwerken van je verzoek. Probeer het opnieuw.',
    });
}

/**
 * The code to assume when the body carried no envelope.
 *
 * This used to be `status >= 500 ? 'server_error' : 'conflict'`, which called a
 * bodyless 404 a conflict and a 401 a conflict too. Anything branching on the
 * code — `isNotFound`, `isValidation`, `isUnauthenticated` — then read the wrong
 * answer for precisely the responses that do not come from the application: a
 * proxy's own 404, a gateway's 502, an upstream 401. The status is the only
 * thing such a response tells us, so it is what the code comes from.
 */
function codeForStatus(status: number): ApiErrorCode {
    switch (status) {
        case 401:
            return 'unauthenticated';
        case 403:
            return 'forbidden';
        case 404:
            return 'not_found';
        case 405:
            return 'method_not_allowed';
        case 409:
            return 'conflict';
        case 413:
            return 'payload_too_large';
        /*
         * 422 deliberately falls through to `server_error`.
         *
         * `validation_failed` is a promise that there are field messages to
         * render, and `formErrorFrom` keeps quiet above the form because the
         * fields will speak for themselves. A 422 with no envelope has no field
         * messages, so claiming the code would hide the failure completely —
         * which is the same empty box this pass exists to remove, one layer
         * down. Laravel always sends a body with a 422; if one arrives without,
         * something other than the application answered.
         */
        case 429:
            return 'too_many_requests';
        default:
            return 'server_error';
    }
}

/** A transport-level failure: offline, DNS, CORS, aborted connection. */
export function networkError(): ApiError {
    return new ApiError(0, {
        code: 'network_error',
        message: 'We kunnen ThuisCoach nu niet bereiken. Controleer je internetverbinding.',
    });
}
